// /src/Courier/CourierDashboard/CourierLayout.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Snackbar,
} from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";

import CourierLocationTracker from "./CourierLocationTracker";
import TopNav from "../../navigation/TopNav";
import SectionNav from "../../navigation/SectionNav";
import MainContent from "../../navigation/MainContent";
import { NAV } from "../../navigation/navigation.config";
import { API } from "../../config";

const getTokenPayload = (token) => {
  try {
    if (!token || !token.includes(".")) return null;

    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    return JSON.parse(window.atob(padded));
  } catch (error) {
    console.error("Unable to decode account token:", error);
    return null;
  }
};

/**
 * SAFELY determine which section is active based on the current URL.
 * This works even when TopNav routes redirect to deeper pages.
 */
function getActiveSectionKeySafe(nav, pathname) {
  if (!nav || !nav.sections) return "dashboard";

  // 1) Try matching by top-level tab base
  for (const tab of nav.top || []) {
    if (pathname === tab.base) return tab.key;
    if (pathname.startsWith(tab.base + "/")) return tab.key;
  }

  // 2) Match by section item paths (this is the critical part)
  for (const [sectionKey, section] of Object.entries(nav.sections)) {
    for (const item of section.items || []) {
      if (pathname === item.path) return sectionKey;
      if (pathname.startsWith(item.path + "/")) return sectionKey;
    }
  }

  // 3) Fallback
  return "dashboard";
}

export default function CourierLayout({ children }) {
  const history = useHistory();
  const location = useLocation();

  const [liveNotification, setLiveNotification] = useState(null);

  // SAFETY: always guard against undefined
  const nav = NAV?.courier;

  const activeSectionKey = useMemo(
    () => getActiveSectionKeySafe(nav, location.pathname),
    [nav, location.pathname]
  );

  const section =
    nav?.sections?.[activeSectionKey] ||
    nav?.sections?.dashboard;

  const handleLogout = async () => {
    try {
      await fetch("/api/logout", { method: "POST" });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("courierType");
      history.push("/courier/sign-in");
    }
  };

  useEffect(() => {
  const token = localStorage.getItem("token");

  if (!token) return undefined;

  const tokenPayload = getTokenPayload(token);

  const currentUserId =
    tokenPayload?.userId ??
    tokenPayload?.user_id ??
    tokenPayload?.sub ??
    tokenPayload?.id ??
    null;

  const currentCourierId =
    tokenPayload?.courierId ??
    tokenPayload?.courier_id ??
    null;

  const events = new EventSource(`${API}/api/events`);

  const handleNegotiationEvent = async (event, type) => {
    try {
      const data = JSON.parse(event.data);
      const threadId = data?.thread_id;

      if (!threadId) return;

      if (
        currentCourierId &&
        Number(data?.courier_id) !== Number(currentCourierId)
      ) {
       return;
      }

      const currentThreadPath =
        `/courier/negotiations/thread/${threadId}`;

      if (location.pathname === currentThreadPath) {
        return;
      }

      // Only notify the courier about customer activity.
      if (
        type === "message" &&
        data?.message?.sender_role !== "customer"
      ) {
        return;
      }

      if (
        type === "offer" &&
        data?.offer?.proposer_role !== "customer"
      ) {
        return;
      }

      // Confirm this courier is allowed to access this thread.
      const response = await fetch(
        `${API}/api/negotiations/thread/${threadId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) return;

      const thread = await response.json();

      const unreadKey =
        "hagglefreight_courier_unread_negotiations";

      const existingUnread = JSON.parse(
        localStorage.getItem(unreadKey) || "{}"
      );

      existingUnread[String(threadId)] = {
        threadId,
        orderId: thread?.order_id,
        orderNumber: thread?.order_number,
        type,
        createdAt: Date.now(),
      };

      localStorage.setItem(
        unreadKey,
        JSON.stringify(existingUnread)
      );

      window.dispatchEvent(
        new CustomEvent(
          "hagglefreight-courier-unread-changed"
        )
      );

      setLiveNotification({
        type,
        threadId,
        orderNumber:
          thread?.order_number || `ORD-${thread?.order_id}`,
      });
    } catch (error) {
      console.error(
        "Courier live negotiation notification failed:",
        error
      );
    }
  };

  const handleAccountStatus = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (
        !currentUserId ||
        Number(data?.user_id) !== Number(currentUserId)
      ) {
        return;
      }

      if (data?.suspended === true) {
        events.close();

        localStorage.removeItem("token");
        localStorage.removeItem("courierType");

        alert(
          "Your HaggleFreight account has been suspended. Please contact support if you believe this was a mistake."
        );

        history.replace("/courier/sign-in");
      }
    } catch (error) {
      console.error("Invalid account status event:", error);
    }
  };

  const handleMessage = (event) => {
    handleNegotiationEvent(event, "message");
  };

  const handleOffer = (event) => {
    handleNegotiationEvent(event, "offer");
  };

  events.addEventListener(
    "account_status",
    handleAccountStatus
  );

  events.addEventListener(
    "negotiation_message",
    handleMessage
  );

  events.addEventListener(
    "negotiation_offer",
    handleOffer
  );

  return () => {
    events.removeEventListener(
      "negotiation_message",
      handleMessage
    );

    events.removeEventListener(
      "negotiation_offer",
      handleOffer
    );

    events.removeEventListener(
      "account_status",
      handleAccountStatus
    );

    events.close();
  };
}, [location.pathname]);

  const rightSlot = (
    <Button color="inherit" onClick={handleLogout}>
      Logout
    </Button>
  );

  return (
    <Box style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Always-on GPS tracking */}
      <CourierLocationTracker />

      {/* Top navigation */}
     <TopNav
         navTop={nav?.top || []}
         appTitle="HaggleFreight"
         rightSlot={rightSlot}
         activeKey={activeSectionKey}
     />

      {/* Sidebar + main content */}
     <Box
       sx={{
         display: "flex",
         flexDirection: { xs: "column", md: "row" },
         flex: 1,
         minHeight: 0,
       }}
>
              <SectionNav
          title={section?.title || "Menu"}
          items={section?.items || []}
        />

        <MainContent>{children}</MainContent>
      </Box>

      <Snackbar
        open={Boolean(liveNotification)}
        autoHideDuration={8000}
        onClose={() => setLiveNotification(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          mt: {
            xs: "calc(env(safe-area-inset-top) + 72px)",
            md: 2,
          },
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setLiveNotification(null)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (!liveNotification?.threadId) return;

                const threadId = liveNotification.threadId;

                setLiveNotification(null);

                history.push(
                  `/courier/negotiations/thread/${threadId}`
                );
              }}
            >
              VIEW
            </Button>
          }
          sx={{
            width: "100%",
            alignItems: "center",
          }}
        >
          {liveNotification?.type === "offer"
            ? `New customer offer for ${liveNotification?.orderNumber}.`
            : `New customer message for ${liveNotification?.orderNumber}.`}
        </Alert>
      </Snackbar>
    </Box>
  );
}
